import React, { useEffect } from 'react';
import { ButtonBase } from '@mui/material';
import { ExitToApp } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useLocalStorage } from 'usehooks-ts';
import styled from 'styled-components';
import i18n from '../../../../../i18n/config';
import { authProvider } from '../../../../../authProvider';

// Shown in their own language, so they are deliberately not translated.
const SUPPORTED_LANGUAGES: Record<string, string> = {
  fi: 'Suomi',
  en: 'English',
};

const UserNav: React.FC = () => {
  const { t } = useTranslation();
  const [language, setLanguage] = useLocalStorage<string>('userPreferance.lang', 'fi');
  const email = authProvider.getAccountInfo()?.username;

  useEffect(() => {
    i18n.changeLanguage(language || 'fi');
  }, [language]);

  return (
    <UserNavRoot>
      <LanguageRow>
        {Object.entries(SUPPORTED_LANGUAGES).map(([lng, name]) => {
          const selected = language === lng;
          return (
            <LanguageButton key={lng} aria-pressed={selected} $selected={selected} onClick={() => setLanguage(lng)}>
              {name}
            </LanguageButton>
          );
        })}
      </LanguageRow>
      <AccountRow>
        {email && <Email>{email}</Email>}
        <LogoutButton onClick={() => authProvider.logout()}>
          <ExitToApp />
          {t('Log out')}
        </LogoutButton>
      </AccountRow>
    </UserNavRoot>
  );
};

const UserNavRoot = styled.div`
  color: ${({ theme }) => theme.palette.common.white};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

const LanguageRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const LanguageButton = styled(ButtonBase)<{ $selected: boolean }>`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: ${({ theme, $selected }) =>
    $selected ? theme.typography.fontWeightBold : theme.typography.fontWeightRegular};
  opacity: ${({ $selected }) => ($selected ? 1 : 0.8)};
  padding-bottom: 2px;
  border-bottom: 2px solid ${({ $selected }) => ($selected ? 'currentColor' : 'transparent')};

  &:hover {
    opacity: 1;
  }
`;

// Who is signed in, right above the way out.
const AccountRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

const Email = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  opacity: 0.8;
  overflow-wrap: anywhere;
`;

const LogoutButton = styled(ButtonBase)`
  align-self: flex-start;
  gap: 8px;
  font-size: ${({ theme }) => theme.fontSizes.lg};

  &:hover {
    opacity: 0.8;
  }

  & .MuiSvgIcon-root {
    font-size: 24px;
  }
`;

export default UserNav;
